/**
 * Lookup columns service for AskAsper
 * Fetches distinct (column_name, entity_name) pairs from the backend LookupValues
 * table, used to power the slash-command (/) suggestion dropdown in the input box.
 *
 * Results are cached in localStorage with a 1-week TTL so the AskAsper page does not
 * have to wait on the network on every mount. The cache is invalidated automatically
 * once it exceeds the TTL.
 */

import { ASK_ASPER_BASE_URL } from '../config';

export interface LookupItem {
    column_name: string;
    entity_name: string;
}

interface LookupColumnsApiResponse {
    count?: number;
    items?: LookupItem[];
}

/** localStorage key for the cached items + fetch timestamp. */
const LOOKUP_CACHE_KEY = 'AskAsper3_LookupColumnsCache_v1';
/** Cache TTL: 7 days. */
const LOOKUP_CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000;

interface LookupCacheEntry {
    fetched_at: number;
    items: LookupItem[];
}

function readLookupCache(): LookupItem[] | null {
    try {
        const raw = localStorage.getItem(LOOKUP_CACHE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as LookupCacheEntry | null;
        if (!parsed || !parsed.fetched_at || !Array.isArray(parsed.items)) return null;
        if (Date.now() - parsed.fetched_at > LOOKUP_CACHE_TTL_MS) return null;
        return parsed.items;
    } catch {
        return null;
    }
}

function writeLookupCache(items: LookupItem[]): void {
    try {
        const entry: LookupCacheEntry = { fetched_at: Date.now(), items };
        localStorage.setItem(LOOKUP_CACHE_KEY, JSON.stringify(entry));
    } catch {
        // Quota / private mode: ignore — the cache is best-effort.
    }
}

/** Clear the cached lookup-columns entry (e.g. on user logout or manual refresh). */
export function clearLookupColumnsCache(): void {
    try {
        localStorage.removeItem(LOOKUP_CACHE_KEY);
    } catch {
        // ignore
    }
}

function getAuthHeader(): string {
    const token = localStorage.getItem('accessToken');
    return `Bearer ${token}`;
}

async function fetchLookupColumnsFromNetwork(): Promise<LookupItem[]> {
    const response = await fetch(`${ASK_ASPER_BASE_URL}lookup-columns`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: getAuthHeader(),
        },
    });

    if (!response.ok) {
        throw new Error(`Error: ${response.status} ${response.statusText}`);
    }

    const contentType = response.headers.get('Content-Type');
    if (!contentType || !contentType.includes('application/json')) {
        return [];
    }

    const raw = (await response.json()) as LookupColumnsApiResponse | LookupItem[];
    const items = Array.isArray(raw) ? raw : raw?.items ?? [];
    if (!Array.isArray(items)) return [];

    return items
        .map((it) => ({
            column_name: String((it as LookupItem).column_name ?? ''),
            entity_name: String((it as LookupItem).entity_name ?? ''),
        }))
        .filter((it) => it.column_name && it.entity_name);
}

/**
 * Fetch distinct (column_name, entity_name) pairs for the slash-command lookup.
 *
 * Returns cached items immediately when a non-expired cache entry exists; otherwise
 * fetches from `${ASK_ASPER_BASE_URL}lookup-columns` and writes the result to cache.
 *
 * @param forceRefresh When true, bypass cache and refetch from network.
 */
export async function fetchLookupColumns(options?: { forceRefresh?: boolean }): Promise<LookupItem[]> {
    if (!options?.forceRefresh) {
        const cached = readLookupCache();
        if (cached) return cached;
    }
    const items = await fetchLookupColumnsFromNetwork();
    writeLookupCache(items);
    return items;
}
